import React, { useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Dialog, Form, Icon, Loading } from '@ok/okd';

import { toLocale } from '@common/i18n';

import history from '../../common/utils/history';

import { submitRecruitApplication } from './api';
import ApplicationFields from './components/ApplicationFields';
import DetailHeader from './components/DetailHeader';
import {
  useApplicationForm,
  usePositionDetail,
  useRecruitPositionId,
  useScrollRoll,
} from './hooks';

import './submit.less';

const buildApplicationPayload = (positionId, values, formData) => {
  const payload = new FormData();
  payload.append('positionId', positionId);
  payload.append('lastName', values.lastName || '');
  payload.append('firstName', values.firstName || '');
  payload.append('birthday', values.birthday || '');
  payload.append('email', values.email || '');
  payload.append('location', values.location || '');
  // Only append real files; a null CV must not be serialized as "null"
  if (formData.curriculumVitae instanceof File) {
    payload.append('curriculumVitae', formData.curriculumVitae);
  }
  formData.jobResumes
    .filter((resume) => resume !== null)
    .forEach((resume) => payload.append('jobResumes', resume));
  payload.append('expectedAnnualSalary', values.expectedAnnualSalary || '');
  payload.append('remarks', values.comment || '');
  return payload;
};

const showSuccessDialog = (positionId) => {
  const dialog = Dialog.success({
    title: toLocale('recruit_submit_success'),
    confirmText: toLocale('recruit_back'),
    onConfirm: () => {
      dialog.destroy();
      history.push(`/recruit/job/${positionId}`);
    },
  });
};

const showErrorDialog = (text) => {
  const dialog = Dialog.error({
    title: text,
    confirmText: toLocale('recruit_dialog_ok'),
    onConfirm: () => {
      dialog.destroy();
    },
  });
};

const Submit = () => {
  const positionId = useRecruitPositionId();
  const { detail: job } = usePositionDetail(positionId);
  const isRoll = useScrollRoll({ disableOnMobile: true });
  const app = useApplicationForm();
  const {
    form,
    formData,
    onFormValuesChange,
    submitLoading,
    setSubmitLoading,
    canSubmit,
  } = app;

  const isSubmittingRef = useRef(false);

  const onSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      try {
        await form.validateFields();
      } catch (e) {
        return; // errors are shown inline by the Form
      }
      if (!window.grecaptcha) {
        showErrorDialog(toLocale('recruit_recaptcha_load_failed'));
        return;
      }
      setSubmitLoading(true);
      try {
        // grecaptcha is callback-based; wrap in a Promise to await the token
        const token = await new Promise((resolve, reject) => {
          window.grecaptcha.ready(() => {
            window.grecaptcha
              .execute(window.okGlobal.recaptchaSiteKey, { action: 'submit' })
              .then(resolve)
              .catch(reject);
          });
        });
        const payload = buildApplicationPayload(
          positionId,
          form.getFieldsValue(),
          formData
        );
        payload.append('googleRecaptchaResponse', token);
        try {
          await submitRecruitApplication(payload);
          showSuccessDialog(positionId);
        } catch (err) {
          showErrorDialog(err?.msg || err?.message);
        } finally {
          setSubmitLoading(false);
        }
      } catch (e) {
        setSubmitLoading(false);
        showErrorDialog(toLocale('recruit_recaptcha_failed'));
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="container">
      <div className="recruit-submit">
        <DetailHeader
          block="recruit-submit"
          isRoll={isRoll}
          name={job?.name}
          tags={job?.tags}
          breadcrumb={[
            toLocale('recruit_job_openings'),
            toLocale('recruit_submit_apply'),
          ]}
        />
        <div className="recruit-submit__body">
          <div className="recruit-submit__content">
            <div className="recruit-submit__back-row">
              <Button
                className="recruit-submit__back-button"
                category={Button.CATEGORY.outline}
                icon={<Icon className="okds-arrow-chevron-left-centered-md" />}
                iconType={Button.ICON_POSITION.leading}
                onClick={() => {
                  history.push(`/recruit/job/${positionId}`);
                }}
              >
                {toLocale('recruit_submit_to_job_desc')}
              </Button>
            </div>
            <div className="recruit-submit__content-title">
              {toLocale('recruit_submit_apply')}
            </div>
            <div className="recruit-submit__form">
              <Form
                layout={Form.LAYOUT.vertical}
                requiredMark
                form={form}
                onValuesChange={onFormValuesChange}
              >
                <ApplicationFields app={app} />
              </Form>
            </div>
            <div className="recruit-submit__submit-row">
              <Button
                className="recruit-submit__submit-button"
                disabled={!canSubmit || submitLoading}
                onClick={onSubmit}
              >
                {toLocale('recruit_form_submit')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Loading loading={submitLoading} hasMask />
    </div>
  );
};

export default withRouter(Submit);
